// EditEmpleado.jsx - Hooks en orden, sin llamadas condicionales
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../Elements/Button/Button";
import useForm from "../../../Hooks/useForm";
import Loader from "../../Elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import { FORM_EDIT_EMPLEADO } from "../../../Constants/editEmpleado";
import { FormEditEmpleadoSchema } from "../../../Models/Schemas/form-edit-empleado";
import EditDatosPersonales from "./EditDatosPersonales/EditDatosPersonales";
import EditContactoEmergencia from "./EditContactoEmergencia/EditContactoEmergencia";
import EditDatosBancarios from "./EditDatosBancarios/EditDatosBancarios";
import EditDatosPuesto from "./EditDatosPuesto/EditDatosPuesto";
import EditVacaciones from "./EditVacaciones/EditVacaciones";
import EditPermisos from "./EditPermisos/EditPermisos";
import EditIncapacidades from "./EditIncapacidades/EditIncapacidades";
import EditFaltas from "./EditFaltas/EditFaltas";
import "./editEmpleado.css";

const EditEmpleado = () => {
  const [checked, setChecked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [contratoFile, setContratoFile] = useState(null);
  const [uploadingContrato, setUploadingContrato] = useState(false);
  const { idEmpleado } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const fetchIntentadoRef = useRef(false);
  const formInicializadoParaRef = useRef(null);

  const {
    users,
    fetchUpdateEmpleado,
    loadingUpdateEmpleado,
    errorUpdateEmpleado,
    fetchAllEmpleados,
    fetchPresignContrato,
    fetchAttachContrato
  } = useStore();

  // Formulario y validación
  const { values, handleChange, isValid, resetForm } = useForm(
    FORM_EDIT_EMPLEADO,
    FormEditEmpleadoSchema
  );

  // Token para requests autenticados
  const loginData = JSON.parse(localStorage.getItem("logindata") || "{}");
  const token = loginData?.data?.token || "";

  const subirContrato = async () => {
    if (!contratoFile) {
      showToast("error", "Selecciona un PDF primero");
      return;
    }
    try {
      setUploadingContrato(true);
      // 1) URL firmada
      const presign = await fetchPresignContrato(idEmpleado, token);
      // 2) PUT a S3
      await fetch(presign.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: contratoFile,
      });
      // 3) attach en DynamoDB
      const curpUpper = (values.curp || "").toString().trim().toUpperCase();
      const filename = `contrato-${curpUpper || "empleado"}.pdf`;
      await fetchAttachContrato(
        idEmpleado,
        { key: presign.key, filename },
        token
      );
      showToast("success", "Contrato subido y vinculado");
      setContratoFile(null);
    } catch (e) {
      showToast("error", e.message || "Error al subir contrato");
    } finally {
      setUploadingContrato(false);
    }
  };

  // Hook: carga inicial de datos
  useEffect(() => {
    const empleado = users.find(
      (emp) => emp.idEmpleado === idEmpleado
    );
    if (!empleado) {
      if (fetchIntentadoRef.current) {
        // Ya se disparó la carga completa; este re-run del efecto es solo
        // por un re-render de por medio (ej. resetForm cambia de referencia
        // en cada render de Formik). No hay que hacer nada más: la propia
        // promesa de abajo es la única que decide "no encontrado".
        return;
      }
      fetchIntentadoRef.current = true;
      fetchAllEmpleados()
        .then((todos) => {
          const existe = (todos || []).some((emp) => emp.idEmpleado === idEmpleado);
          if (!existe) setNotFound(true);
        })
        .catch(() => setNotFound(true))
        .finally(() => setChecked(true));
    } else {
      setNotFound(false);

      // Ojo: "resetForm" (de Formik) no mantiene la misma referencia entre
      // renders, y está en las dependencias de este efecto -> sin este
      // guard, CADA re-render (incluido cada tecla que el usuario escribe)
      // volvía a correr este bloque y pisaba lo recién editado con los
      // valores originales del empleado. Por eso "Guardar" nunca se
      // habilitaba: el cambio se revertía antes de que se notara.
      if (formInicializadoParaRef.current === idEmpleado) {
        setChecked(true);
        return;
      }
      formInicializadoParaRef.current = idEmpleado;

      const periodos = empleado.datosPuesto?.periodos || [];
      const ultimoPeriodo = periodos[periodos.length - 1] || {};
      const flat = {
        idEmpleado: empleado.idEmpleado || "",
        numeroEmpleado: empleado.numeroEmpleado || "",
        nombre: empleado.datosPersonales?.nombre || "",
        apellidoPaterno: empleado.datosPersonales?.apellidoPaterno || "",
        apellidoMaterno: empleado.datosPersonales?.apellidoMaterno || "",
        curp: empleado.datosPersonales?.curp || "",
        rfc: empleado.datosPersonales?.rfc || "",
        fechaNacimiento: empleado.datosPersonales?.fechaNacimiento || "",
        numeroSeguroSocial: empleado.datosPersonales?.numeroSeguroSocial || "",
        gradoEstudios: empleado.datosPersonales?.gradoEstudios || "",
        contactoEmergenciaNombre: empleado.datosPersonales?.contactosEmergencia?.[0]?.nombre || "",
        contactoEmergenciaParentesco: empleado.datosPersonales?.contactosEmergencia?.[0]?.parentesco || "",
        contactoEmergenciaTelefono: empleado.datosPersonales?.contactosEmergencia?.[0]?.telefono || "",
        contactoEmergenciaTelefonoAlterno: empleado.datosPersonales?.contactosEmergencia?.[0]?.telefonoAlterno || "",
        contactoEmergenciaDireccion: empleado.datosPersonales?.contactosEmergencia?.[0]?.direccion || "",
        clabeNomina: empleado.datosBancarios?.cuentaNomina?.clabe || "",
        bancoNomina: empleado.datosBancarios?.cuentaNomina?.banco || "",
        titularNomina: empleado.datosBancarios?.cuentaNomina?.titular || "",
        clabeAdicional: empleado.datosBancarios?.cuentaAdicional?.clabe || "",
        bancoAdicional: empleado.datosBancarios?.cuentaAdicional?.banco || "",
        titularAdicional: empleado.datosBancarios?.cuentaAdicional?.titular || "",
        puesto: empleado.datosPuesto?.puesto || "",
        area: empleado.datosPuesto?.area || "",
        fechaIngreso: ultimoPeriodo.fechaIngreso || "",
        fechaEgreso: ultimoPeriodo.fechaEgreso || "", // "" (no null) para que el input controlado no truene
        activo: ultimoPeriodo.activo ?? true,
        periodos: empleado.datosPuesto?.periodos || [], // <-- 🔥 ESTO FALTABA
        vacacionFolio: empleado.datosPuesto?.vacacionesTomadas?.[0]?.folio || "",
        vacacionDias: empleado.datosPuesto?.vacacionesTomadas?.[0]?.dias || 0,
        vacacionFechas: empleado.datosPuesto?.vacacionesTomadas?.[0]?.fechas?.join(",") || "",
        vacacionAnio: empleado.datosPuesto?.vacacionesTomadas?.[0]?.anio || new Date().getFullYear(),
        vacacionesTomadas: empleado.datosPuesto?.vacacionesTomadas || [],
        permisos: empleado.datosPuesto?.permisos || [],
        incapacidades: empleado.datosPuesto?.incapacidades || [],
        faltas: empleado.datosPuesto?.faltas || [],
        asistencias: empleado.datosPuesto?.asistencias || [],
      };

      resetForm({ values: flat });
      setChecked(true);
    }
  }, [users, idEmpleado, fetchAllEmpleados, resetForm]);

  // Manejo de errores de actualización
  useEffect(() => {
    if (errorUpdateEmpleado) {
      const msg =
        errorUpdateEmpleado instanceof Error
          ? errorUpdateEmpleado.message
          : errorUpdateEmpleado.error || "Error desconocido";
      showToast("error", msg);
    }
  }, [errorUpdateEmpleado, showToast]);

  // Mostrar loader durante carga o envío
  if (!checked || loadingUpdateEmpleado) {
    return <Loader fullScreen />;
  }

  if (notFound) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center">
          No se encontró el empleado.
        </div>
        <div className="d-flex justify-content-center mt-3">
          <Button text="Regresar" color="gray" fill action={() => navigate(-1)} />
        </div>
      </div>
    );
  }

  const submit = async () => {
    const periodosOriginales = values.periodos || [];
    const activoActual = periodosOriginales.find(p => p.activo);
    const tieneActivo = !!activoActual;

    let nuevosPeriodos = [];

    // RECONTRATACIÓN: no hay activo y ahora values.activo === true
    if (!tieneActivo && values.activo) {
      // dejar todo el historial como inactivo y agregar uno nuevo activo
      const historial = periodosOriginales.map(p => ({ ...p, activo: false }));
      nuevosPeriodos = [
        ...historial,
        {
          fechaIngreso: values.fechaIngreso,
          fechaEgreso: null,
          activo: true,
        },
      ];
    }
    // EGRESO / BAJA: hay activo y ahora values.activo === false
    else if (tieneActivo && !values.activo) {
      nuevosPeriodos = periodosOriginales.map((p) => {
        if (p.activo) {
          return {
            ...p,
            fechaEgreso: values.fechaEgreso,
            activo: false,
          };
        }
        return p;
      });
    }
    // EDICIÓN DE INGRESO O MANTENER ACTIVO: hay activo y sigue activo
    else if (tieneActivo && values.activo) {
      nuevosPeriodos = periodosOriginales.map((p) => {
        if (p.activo) {
          return {
            ...p,
            fechaIngreso: values.fechaIngreso,
            // conserva fechaEgreso si existiera (aunque no debería en activo)
            fechaEgreso: p.fechaEgreso || null,
            activo: true,
          };
        }
        return p;
      });
    } else {
      // caso por default: copiar tal cual (ej. sin cambios detectables)
      nuevosPeriodos = [...periodosOriginales];
    }

    const body = {
      numeroEmpleado: values.numeroEmpleado || null, // <-- nuevo, editable
      datosPersonales: {
        nombre: values.nombre,
        apellidoPaterno: values.apellidoPaterno,
        apellidoMaterno: values.apellidoMaterno,
        curp: values.curp,
        rfc: values.rfc,
        fechaNacimiento: values.fechaNacimiento,
        numeroSeguroSocial: values.numeroSeguroSocial,
        gradoEstudios: values.gradoEstudios,
        contactosEmergencia: [
          {
            nombre: values.contactoEmergenciaNombre,
            parentesco: values.contactoEmergenciaParentesco,
            telefono: values.contactoEmergenciaTelefono,
            telefonoAlterno: values.contactoEmergenciaTelefonoAlterno,
            direccion: values.contactoEmergenciaDireccion,
          },
        ],
      },
      datosBancarios: {
        cuentaNomina: {
          clabe: values.clabeNomina,
          banco: values.bancoNomina,
          titular: values.titularNomina,
        },
        cuentaAdicional: {
          clabe: values.clabeAdicional,
          banco: values.bancoAdicional,
          titular: values.titularAdicional,
        },
      },
      datosPuesto: {
        puesto: values.puesto,
        area: values.area,
        periodos: nuevosPeriodos,
        vacacionesTomadas: values.vacacionesTomadas || [],
        permisos: values.permisos || [],
        incapacidades: values.incapacidades || [],
        faltas: values.faltas || [],
        asistencias: values.asistencias || []
      },
    };

    try {
      await fetchUpdateEmpleado(idEmpleado, body, token);
      showToast("success", "Empleado actualizado correctamente");
      navigate("/", { replace: true });
    } catch (err) {
      showToast("error", err.message || "Hubo un error al actualizar");
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-9 col-lg-7">
          <div className="card shadow-lg border-0 rounded-4 p-4">
            <hr />
            <h5 className="mb-2">Contrato (PDF)</h5>
            <div className="row g-3">
              <div className="col-md-8">
                <input
                  type="file"
                  accept="application/pdf"
                  className="form-control"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && f.type !== "application/pdf") {
                      showToast("error", "El archivo debe ser PDF");
                      e.target.value = "";
                      return;
                    }
                    if (f && f.size > 10 * 1024 * 1024) {
                      showToast("error", "El PDF no puede superar 10MB");
                      e.target.value = "";
                      return;
                    }
                    setContratoFile(f);
                  }}
                />
              </div>
              <div className="col-md-4 d-flex">
                <Button
                  text={uploadingContrato ? "Subiendo..." : "Subir contrato"}
                  color="blue"
                  fill
                  action={subirContrato}
                  disabled={!contratoFile || uploadingContrato}
                  classNames="w-100"
                />
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="mb-0 fw-bold text-primary-emphasis">Editar Empleado</h2>
              <Button
                text="Cancelar"
                color="gray"
                fill
                classNames="btn-cancelar"
                action={() => navigate(-1)}
              />
            </div>
            <form className="edit-empleado-form-fields" autoComplete="off">
              <EditDatosPersonales values={values} handleChange={handleChange} />
              <EditContactoEmergencia values={values} handleChange={handleChange} />
              <EditDatosBancarios values={values} handleChange={handleChange} />
              <EditDatosPuesto values={values} handleChange={handleChange} />
              <EditVacaciones values={values} handleChange={handleChange} />
              <EditPermisos values={values} handleChange={handleChange} />
              <EditIncapacidades values={values} handleChange={handleChange} />
              <EditFaltas values={values} handleChange={handleChange} />
              <div className="d-flex justify-content-end mt-4">
                <Button
                  text="Guardar cambios"
                  color="green"
                  fill
                  action={submit}
                  disabled={!isValid || uploadingContrato}
                  classNames="edit-empleado-btn"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEmpleado;